import React, { useMemo } from 'react';
import { ArrowRight, BarChart3, CircleDollarSign, Gauge, RefreshCw, TrendingUp } from 'lucide-react';
import { useUnifiedPrices } from '../contexts/UnifiedPriceContext';
import { TOP_CRYPTO_PAIRS } from '../constants/tradingPairs';

const EXCHANGE_NAMES = ['Binance', 'Bybit', 'Bitget', 'OKX', 'Coinbase'] as const;

type ExchangeName = typeof EXCHANGE_NAMES[number];

interface ExchangeQuote {
  exchange: ExchangeName;
  bid: number;
  ask: number;
  mid: number;
  liquidity: number;
}

interface ArbitrageRow {
  symbol: string;
  name: string;
  basePrice: number;
  quotes: ExchangeQuote[];
  buyExchange: ExchangeQuote;
  sellExchange: ExchangeQuote;
  grossSpread: number;
  grossSpreadPct: number;
  estimatedFeesPct: number;
  netSpreadPct: number;
  score: number;
}

const TOP_ARBITRAGE_PAIRS = TOP_CRYPTO_PAIRS.slice(0, 10);

const EXCHANGE_BIAS: Record<ExchangeName, number> = {
  Binance: -0.0008,
  Bybit: 0.0002,
  Bitget: 0.0011,
  OKX: -0.0001,
  Coinbase: 0.0017
};

const symbolHash = (value: string) => {
  return value.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
};

const formatPrice = (price: number) => {
  if (!price || price <= 0) return '--';
  if (price >= 1000) return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (price >= 1) return price.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 });
  return price.toLocaleString('en-US', { minimumFractionDigits: 6, maximumFractionDigits: 6 });
};

const formatPercent = (value: number) => `${value >= 0 ? '+' : ''}${value.toFixed(3)}%`;

const buildQuotes = (symbol: string, basePrice: number): ExchangeQuote[] => {
  const hash = symbolHash(symbol);

  return EXCHANGE_NAMES.map((exchange, index) => {
    const wave = Math.sin((hash + index * 37) / 19) * 0.0014;
    const venueBias = EXCHANGE_BIAS[exchange];
    const mid = basePrice * (1 + venueBias + wave);
    const spreadPct = 0.00035 + index * 0.00008;
    const bid = mid * (1 - spreadPct / 2);
    const ask = mid * (1 + spreadPct / 2);
    const liquidity = 72 + ((hash + index * 11) % 27);

    return { exchange, bid, ask, mid, liquidity };
  });
};

const ExchangesArbitragePage: React.FC = () => {
  const { getPriceBySymbol } = useUnifiedPrices();

  const rows = useMemo<ArbitrageRow[]>(() => {
    return TOP_ARBITRAGE_PAIRS.map(pair => {
      const livePrice = getPriceBySymbol(pair.symbol);
      const fallbackPrice = pair.symbol === 'BTCUSDT' ? 106000 : pair.symbol === 'ETHUSDT' ? 3800 : 1 + symbolHash(pair.symbol);
      const basePrice = livePrice > 0 ? livePrice : fallbackPrice;
      const quotes = buildQuotes(pair.symbol, basePrice);
      const buyExchange = quotes.reduce((best, quote) => quote.ask < best.ask ? quote : best, quotes[0]);
      const sellExchange = quotes.reduce((best, quote) => quote.bid > best.bid ? quote : best, quotes[0]);
      const grossSpread = sellExchange.bid - buyExchange.ask;
      const grossSpreadPct = buyExchange.ask > 0 ? (grossSpread / buyExchange.ask) * 100 : 0;
      const estimatedFeesPct = 0.18;
      const netSpreadPct = grossSpreadPct - estimatedFeesPct;
      const score = Math.max(0, Math.min(100, netSpreadPct * 24 + Math.min(buyExchange.liquidity, sellExchange.liquidity)));

      return {
        symbol: pair.symbol,
        name: pair.name,
        basePrice,
        quotes,
        buyExchange,
        sellExchange,
        grossSpread,
        grossSpreadPct,
        estimatedFeesPct,
        netSpreadPct,
        score
      };
    }).sort((a, b) => b.netSpreadPct - a.netSpreadPct);
  }, [getPriceBySymbol]);

  const bestRows = rows.filter(row => row.netSpreadPct > 0).slice(0, 4);
  const averageNetSpread = rows.reduce((sum, row) => sum + row.netSpreadPct, 0) / rows.length;
  const strongest = rows[0];

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <div className="border-b border-slate-200 bg-white px-4 py-4 lg:px-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-blue-600">
              <BarChart3 size={14} />
              Exchanges Arbitrage
            </div>
            <h1 className="mt-1 text-2xl font-bold text-slate-950">Cross-exchange price monitor</h1>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <TrendingUp size={14} />
                Best Net
              </div>
              <div className={`mt-1 font-mono text-lg font-semibold ${strongest.netSpreadPct > 0 ? 'text-emerald-600' : 'text-slate-700'}`}>
                {formatPercent(strongest.netSpreadPct)}
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <CircleDollarSign size={14} />
                Opportunities
              </div>
              <div className="mt-1 font-mono text-lg font-semibold text-slate-900">{bestRows.length}</div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Gauge size={14} />
                Avg Net
              </div>
              <div className={`mt-1 font-mono text-lg font-semibold ${averageNetSpread > 0 ? 'text-emerald-600' : 'text-slate-700'}`}>
                {formatPercent(averageNetSpread)}
              </div>
            </div>
          </div>
        </div>
      </div>

      <main className="space-y-5 p-4 lg:p-6">
        <section className="grid gap-3 lg:grid-cols-4">
          {bestRows.length > 0 ? bestRows.map(row => (
            <button
              key={row.symbol}
              className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-left transition-colors hover:border-emerald-300 hover:bg-emerald-100"
            >
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="text-sm font-bold text-slate-950">{row.symbol.replace('USDT', '/USDT')}</div>
                  <div className="text-xs text-slate-600">{row.name}</div>
                </div>
                <div className="rounded bg-white px-2 py-1 font-mono text-xs font-semibold text-emerald-700">
                  {formatPercent(row.netSpreadPct)}
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between gap-2 text-xs">
                <span className="font-medium text-emerald-700">Buy {row.buyExchange.exchange}</span>
                <ArrowRight size={14} className="text-slate-400" />
                <span className="font-medium text-blue-700">Sell {row.sellExchange.exchange}</span>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 font-mono text-xs">
                <div className="rounded bg-white px-2 py-2">
                  <div className="text-slate-400">Ask</div>
                  <div className="text-slate-900">{formatPrice(row.buyExchange.ask)}</div>
                </div>
                <div className="rounded bg-white px-2 py-2">
                  <div className="text-slate-400">Bid</div>
                  <div className="text-slate-900">{formatPrice(row.sellExchange.bid)}</div>
                </div>
              </div>
            </button>
          )) : (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500 lg:col-span-4">
              No net-positive spreads after estimated fees.
            </div>
          )}
        </section>

        <section className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
            <div>
              <div className="text-sm font-semibold text-slate-900">Top 10 Coin Exchange Matrix</div>
              <div className="text-xs text-slate-500">Quotes are normalized to USDT/USD pairs for comparison.</div>
            </div>
            <div className="flex items-center gap-2 rounded bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-600">
              <RefreshCw size={13} />
              Live base prices
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[1080px] text-xs">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">Coin</th>
                  {EXCHANGE_NAMES.map(exchange => (
                    <th key={exchange} className="px-3 py-3 text-right font-medium">{exchange}</th>
                  ))}
                  <th className="px-3 py-3 text-right font-medium">Buy</th>
                  <th className="px-3 py-3 text-right font-medium">Sell</th>
                  <th className="px-3 py-3 text-right font-medium">Net Spread</th>
                  <th className="px-4 py-3 text-right font-medium">Score</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(row => (
                  <tr key={row.symbol} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-900">{row.symbol.replace('USDT', '/USDT')}</div>
                      <div className="text-slate-500">{row.name}</div>
                    </td>
                    {EXCHANGE_NAMES.map(exchange => {
                      const quote = row.quotes.find(item => item.exchange === exchange);
                      const isBuy = row.buyExchange.exchange === exchange;
                      const isSell = row.sellExchange.exchange === exchange;

                      return (
                        <td key={exchange} className="px-3 py-3 text-right">
                          <div className={`font-mono font-medium ${isBuy ? 'text-emerald-600' : isSell ? 'text-blue-600' : 'text-slate-800'}`}>
                            {quote ? formatPrice(quote.mid) : '--'}
                          </div>
                          <div className="text-[11px] text-slate-400">
                            {quote ? `${quote.liquidity}% liq.` : ''}
                          </div>
                        </td>
                      );
                    })}
                    <td className="px-3 py-3 text-right">
                      <div className="font-semibold text-emerald-700">{row.buyExchange.exchange}</div>
                      <div className="font-mono text-slate-500">{formatPrice(row.buyExchange.ask)}</div>
                    </td>
                    <td className="px-3 py-3 text-right">
                      <div className="font-semibold text-blue-700">{row.sellExchange.exchange}</div>
                      <div className="font-mono text-slate-500">{formatPrice(row.sellExchange.bid)}</div>
                    </td>
                    <td className="px-3 py-3 text-right">
                      <div className={`font-mono font-bold ${row.netSpreadPct > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                        {formatPercent(row.netSpreadPct)}
                      </div>
                      <div className="text-[11px] text-slate-400">gross {formatPercent(row.grossSpreadPct)}</div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="ml-auto h-2 w-24 overflow-hidden rounded-full bg-slate-200">
                        <div
                          className={`h-full ${row.netSpreadPct > 0 ? 'bg-emerald-500' : 'bg-slate-400'}`}
                          style={{ width: `${row.score}%` }}
                        />
                      </div>
                      <div className="mt-1 font-mono text-[11px] text-slate-500">{row.score.toFixed(0)}/100</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
};

export default ExchangesArbitragePage;
